/**
 * @file_purpose  page created for provider details page
 * @author Khusir Alam
 * @Date_Created 28-02-2024
 * @Date_Modified 28-05-2025
 */

import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useLocation } from 'react-router-dom';
import GeneralService from '../../apis/GeneralService';
import ApiURL from '../../apis/ApiURL';
import Constants from '../../utility/Constants';
import { listGame, resetListGame } from '../../redux/slices/GameSlice';
import { FormattedMessage } from "react-intl";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import { getSelectedDevice } from '../../utility/Helpers';

const ProviderDetails = () => {

    const dispatch = useDispatch();
    const location = useLocation();
    let provider_id = location.state.provider_id;
    let provider_name = location.state.provider_name;

    let gameState = useSelector((state) => state.gameState);
    const userDetailState = useSelector((state) => state.userDetailState);
    const languageState = useSelector((state) => state.languageState);
    const accountList = useSelector((state) => state.accountState.account_list);
    const loginCreds = useSelector((state) => state.authState.logincreds);
    let token = userDetailState?.user_details?.token || "";
    let languageCode = languageState.active_language.language_code;
    const gameLaunchLang = useSelector((state) => state.gameLaunchLanguageState.selected_language);
    //console.log("gameLaunchLang===>", gameLaunchLang)
    let [loading, setLoading] = useState(true);
    let [launching, setLaunching] = useState(false); // for animation
    let newPage = 1;
    let [page, setPage] = useState(1);
    let [limit, setLimit] = useState(24);
    let [totalPage, setTotalPage] = useState(1);

    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedImportAccount, setSelectedImportAccount] = useState("");
    const [importing, setImporting] = useState(false);

    const [state, setState] = useState({
        game_name: "",
        is_popular: ""
    })

    const providerAccounts = accountList?.filter(acc => acc.provider_id === provider_id || acc.provider === provider_id) || [];
    const displayAccounts = loginCreds?.account_id
        ? (accountList || []).filter(acc => acc._id === loginCreds.account_id)
        : (providerAccounts.length > 0 ? providerAccounts : (accountList || []));

    const handleImportGames = async () => {
        if (!selectedImportAccount) {
            toast.error("Please select an account first.", { position: "bottom-right" });
            return;
        }

        setImporting(true);
        const params = JSON.stringify({
            provider_id: provider_id,
            account_id: selectedImportAccount
        });

        GeneralService.apiPostCallRequest(ApiURL.importGames, params, token)
            .then(res => {
                setImporting(false);
                if (res.error == Constants.API_RESPONSE_ERROR_FALSE) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Import Successful',
                        text: res.message || 'Games imported successfully!',
                    });
                    setShowImportModal(false);
                    // Refresh game list
                    setLoading(true);
                    setPage(1);
                    dispatch(resetListGame());
                    loadGameList();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Import Failed',
                        text: res.message || 'Failed to import games.',
                    });
                }
            })
            .catch(err => {
                setImporting(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Import Failed',
                    text: err.message || 'An error occurred during import.',
                });
            });
    };

    const [useCommonCallback, setUseCommonCallback] = useState(() => {
        return localStorage.getItem('useCommonCallback') === 'true';
    });

    useEffect(() => {
        dispatch(resetListGame())
    }, [location.pathname])

    useEffect(() => {
        localStorage.setItem('useCommonCallback', useCommonCallback);
    }, [useCommonCallback])

    async function loadGameList() {
        let params = JSON.stringify({
            "provider_id": provider_id,
            "game_name": state.game_name,
            "category_id": "",
        })

        GeneralService.apiPostCallRequest(`${ApiURL.getGameList}?page=${newPage}&limit=${limit}`, params, token).then(res => {
            if (res.error == Constants.API_RESPONSE_ERROR_FALSE) {
                setLoading(false)
                setTotalPage(res.data.pages)
                dispatch(listGame(res.data.games))
            }
        }).catch(() => setLoading(false))
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        setPage(1)
        dispatch(resetListGame())
        loadGameList();
    }, [provider_id, state.game_name])

    const handleChange = (e) => {
        const { id, value } = e.target;
        setLoading(true)
        setState(prevState => ({
            ...prevState,
            [id]: value
        }))
    }

    const loadMoreClick = () => {
        newPage = page + 1;
        setPage(newPage);
        loadGameList();
    }

    const gameLunchHandler = (game_code, gameData, gamePlayMode) => {
        setLaunching(true); // Show animation

        let params = JSON.stringify({
            "game_code": game_code,
            "language": gameLaunchLang,
            "game_mode": gamePlayMode,
            "device": getSelectedDevice()
        });

        if (!token) {
            setLaunching(false);
            Swal.fire({
                icon: 'error',
                title: 'Launch Failed',
                text: 'Please Login to play',
            });
        } else {
            const isSpecialProvider = ['Evolution', 'Red Tiger', 'NetEnt', 'Big Time Gaming', 'Nolimit City - 94', 'Nolimit City - 96', 'Tap-A-Roo', 'Nolimit City - 92', 'Nolimit City'].includes(gameData.provider_name);
            // Use common callback (REACT_APP_CLIENT_API_BASE_URL) when toggle is enabled
            const apiCall = useCommonCallback ? GeneralService.apiPostCallRequest : (isSpecialProvider ? GeneralService.evoApiPostCallRequest : GeneralService.apiPostCallRequest);

            apiCall(ApiURL.getGameURL, params, token).then(res => {
                setLaunching(false);
                const url = res?.entry || res?.entryEmbedded || res?.data?.url;
                if (url && typeof url === 'string' && url !== 'undefined') {
                    window.open(url, "_blank", "noreferrer");
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Launch Failed',
                        text: 'Unable to launch the game',
                    });
                }
            }).catch(error => {
                setLaunching(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Launch Failed',
                    text: error.message,
                });
            });
        }
    }

    const demoPlayProviders = ["Boongo", "Pragmatic Play", "NetEnt", "Big Time Gaming", "Red Tiger", "Nolimit City - 94", "Nolimit City - 96", "Tap-A-Roo", "Jili", "Betsoft", "Qora", "Evoplay", "Slotopia", "Novomatic", "Penguin King", "Play N Go", "Triple Profits Games", "JDB", "Allbet", "BGaming", "Dragoon Soft", "Expanse", "Skywind", "1X2 Gaming", "Dream Gaming", "GamePlay Interactive", "Microgaming Live", "AvatarUX", "Blueprint Gaming", "Relax Gaming", "Hacksaw Gaming", "Slotmill", "Yggdrasil", "Fantasma", "Habanero", "WorldMatch", "Sigma", "Kalamba", "Mobilots", "OneTouch", "Revolver Gaming", "Realtime Gaming", "PLAYSTAR", "Simpleplay", "Nolimit City - 92", "WonWon", "QoraGenesis", "Qora", "Iron Dog Studio", "Kiron", "WM Casino", "Quickspin", "Thunderkick", "CosmoPlay", "EEAI"];

    return (
        <div className='container'>
            {/* Launching Overlay Animation */}
            {launching && (
                <div className="launch-overlay">
                    <div className="launch-spinner"></div>
                    <span className="launch-text">Launching your game...</span>
                </div>
            )}

            <div className='provider-heading-section'>
                <span>{provider_name}</span>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button className='import-game-button' onClick={() => {
                        setSelectedImportAccount(loginCreds?.account_id || "");
                        setShowImportModal(true);
                    }}>
                        <i className="fa-solid fa-cloud-arrow-down me-2"></i> Import Games
                    </button>
                    <Link to="/"><button className='back-button'>
                        <i className="fa-solid fa-arrow-left me-2"></i> <FormattedMessage id='BACK' />
                    </button></Link>
                </div>
            </div>

            <div className='provider-detail-page-section'>
                <div className='search_filter_section'>
                    <div className='provider-filter-section'>
                        <Link to="/provider-list">
                            <button className='view-all-providers' style={{ marginRight: "10px" }}>
                                <FormattedMessage id='VIEWALLPROVIDERS' />
                            </button>
                        </Link>
                    </div>
                    {['Evolution', 'Red Tiger', 'NetEnt', 'Big Time Gaming', 'Nolimit City - 94', 'Nolimit City - 96', 'Tap-A-Roo', 'Nolimit City - 92'].includes(provider_name) && (
                        <div className='common-callback-toggle'>
                            <label className='toggle-label'>
                                <input
                                    type='checkbox'
                                    checked={useCommonCallback}
                                    onChange={(e) => setUseCommonCallback(e.target.checked)}
                                />
                                <span className='toggle-slider'></span>
                                <span className='toggle-text'>Common Callback</span>
                            </label>
                        </div>
                    )}
                    <div className="game_search_sec">
                        <i className="fa-solid fa-magnifying-glass" />
                        <input type="search" id='game_name' name='game_name' placeholder={languageCode === "en" ? "Search your game" : "게임 검색"} value={state.game_name} onChange={handleChange} />
                    </div>
                </div>

                <div className='povider-game-list-section' id='style-2'>
                    {loading &&
                        <div className='row w-100 d-flex align-items-center justify-content-center'>
                            <div className="spinner-border text-primary" role="status" style={{ marginTop: "20px" }}>
                                <span className="visually-hidden"></span>
                            </div>
                        </div>
                    }

                    {gameState.game_list?.length > 0 && gameState.game_list.map((gameData, gameIndex) => (
                        // <div className='col-2' key={gameIndex} onClick={() => gameLunchHandler(gameData.game_code, gameData)}>
                        //     <div className='each-game-card'>
                        //         <img className='each-game-card-img w-100' src={gameData.image_file_name} alt='game_img' />
                        //         <div className='game-detail-section'>
                        //             <span className='game-detail-playing'>{gameData.game_name[languageCode]}</span>
                        //             <span className='game-provider-name'>{gameData.provider_name[languageCode]}</span>
                        //         </div>
                        //     </div>
                        // </div>

                        <div className='col-2' key={gameIndex}>
                            <div className='each-game-card'>
                                {/* Image */}
                                <img className='each-game-card-img w-100'
                                    src={Constants.PROVIDERS_WITH_RESIZED_IMAGES.includes(gameData.provider_name) ? gameData.image_file_name.replace('/game/', '/250x250/') : gameData.image_file_name}
                                    alt='game_img'
                                />
                                {/* Hover buttons */}
                                <div className="game-hover-actions">

                                    {/* Real Play always visible */}
                                    <button
                                        className="play-btn real-play"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            gameLunchHandler(gameData.game_code, gameData, 'real');
                                        }}
                                    >
                                        <i className="fa-solid fa-play"></i>
                                        Play
                                    </button>

                                    {/* Dummy Play - show only if provider is in allowed list */}
                                    {demoPlayProviders.includes(gameData.provider_name) && (
                                        <button
                                            className="play-btn dummy-play"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                gameLunchHandler(gameData.game_code, gameData, 'fun');
                                            }}
                                        >
                                            <i className="fa-solid fa-gamepad"></i>
                                            Demo Play
                                        </button>
                                    )}

                                </div>

                                {/* Details */}
                                <div className='game-detail-section'>
                                    <span className='game-detail-playing'>{gameData.game_name[languageCode]}</span>
                                    <span className='game-provider-name'>{gameData.provider_name[languageCode]}</span>
                                </div>
                            </div>
                        </div>


                    ))}

                    {(loading === false && gameState.game_list?.length === 0) &&
                        <div className="alert alert-primary no_game_found" role="alert">
                            <FormattedMessage id='NOGAMEFOUND' />
                        </div>
                    }

                    {totalPage !== page &&
                        <div className='load_more_button_div'>
                            <button className='load_more_button' onClick={loadMoreClick} hidden={totalPage === 0}>
                                <FormattedMessage id='LOADMORE' />
                            </button>
                        </div>
                    }
                </div>
            </div>

            <Modal
                show={showImportModal}
                onHide={() => !importing && setShowImportModal(false)}
                backdrop="static"
                centered
            >
                <div className="apply_comp_sec">
                    {importing && (
                        <div className="launch-overlay" style={{ background: 'rgba(0,0,0,0.5)', borderRadius: '15px', position: 'absolute' }}>
                            <div className="launch-spinner"></div>
                            <span className="launch-text">Importing games...</span>
                        </div>
                    )}
                    <Modal.Header closeButton={!importing} className='py-3'>
                        <h6 className="hed_txt m-0">Import Games - {provider_name}</h6>
                    </Modal.Header>

                    <div className="p-4">
                        <div className="row login-register-form">
                            <div className="col-md-12 input-display">
                                <label htmlFor="import_account">
                                    Select Provider Account
                                    <span className='v-error'> * </span>
                                </label>
                                <select
                                    id='import_account'
                                    name='import_account'
                                    value={selectedImportAccount}
                                    onChange={(e) => setSelectedImportAccount(e.target.value)}
                                    disabled={importing || !!loginCreds?.account_id}
                                >
                                    <option value="">Select Account</option>
                                    {displayAccounts.map((item, keyIndex) => (
                                        <option key={keyIndex} value={item._id}>
                                            {item.account_name} ({item.currency_support})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="row pb-4">
                        <div className="col-md-10 offset-md-1 save-cancel-btn">
                            <button
                                className="save_btn"
                                style={{ marginRight: 10 }}
                                onClick={handleImportGames}
                                disabled={importing}
                            >
                                Import
                            </button>
                            <button
                                className="cancel_btn"
                                onClick={() => setShowImportModal(false)}
                                disabled={importing}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Animation CSS */}
            <style>{`
                .launch-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.7);
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 1.5rem;
                }

                .launch-spinner {
                    width: 80px;
                    height: 80px;
                    border: 8px solid #ccc;
                    border-top: 8px solid #4CAF50;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                .launch-text {
                    font-weight: 600;
                    letter-spacing: 1px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                
                .each-game-card {
                    position: relative;
                    overflow: hidden;
                    cursor: pointer;
                }

                .game-hover-actions {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 83%;
                    background: rgba(0,0,0,0.65);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    opacity: 0;
                    transition: opacity .3s ease;
                    gap: 10px;
                }

                .each-game-card:hover .game-hover-actions {
                    opacity: 1;
                }

                .play-btn {
                    background: #ffffff;
                    color: #000;
                    padding: 8px 14px;
                    border-radius: 8px;
                    border: none;
                    font-size: 14px;
                    display: flex;
                    gap: 6px;
                    align-items: center;
                    cursor: pointer;
                    font-weight: 600;
                }

                .play-btn:hover {
                    background: #e6e6e6;
                }

                .real-play {
                    border-left: 4px solid #28a745;
                }

                .dummy-play {
                    border-left: 4px solid #ff9800;
                }

                .provider-controls {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }

                .common-callback-toggle {
                    display: flex;
                    align-items: center;
                    flex: 1;
                    justify-content: center;
                }

                .toggle-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    color: #fff;
                }

                .toggle-label input[type="checkbox"] {
                    display: none;
                }

                .toggle-slider {
                    position: relative;
                    width: 44px;
                    height: 24px;
                    background-color: #ccc;
                    border-radius: 34px;
                    transition: background-color 0.3s;
                }

                .toggle-slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    border-radius: 50%;
                    transition: transform 0.3s;
                }

                .toggle-label input:checked + .toggle-slider {
                    background-color: #28a745;
                }

                .toggle-label input:checked + .toggle-slider:before {
                    transform: translateX(20px);
                }

                .toggle-text {
                    font-weight: 500;
                }

                .import-game-button {
                    cursor: pointer;
                    border: 2px solid #5f6d93;
                    outline: none;
                    background-color: transparent;
                    color: #fff;
                    border-radius: 20px;
                    padding: 5px 15px;
                    font-weight: 500;
                    font-size: 20px;
                    transition: all 0.3s ease;
                }

                .import-game-button:hover {
                    background-color: #5f6d93;
                    border-color: #5f6d93;
                }

            `}</style>
        </div>
    )
}

export default ProviderDetails;
